/**
 * Campo de color
 * -----------------------------------------------------------------------------
 * Un campo que se pinta del color elegido y que al pulsarlo abre los cuadros
 * para escoger.
 *
 *   <div class="form-outline" data-mdb-input-init data-company-color-init
 *        data-company-color-options='[{"value":"rojo","label":"Rojo","color":"#e53935"}]'>
 *     <input type="text" class="form-control" id="corbata" name="corbata" value="rojo" />
 *     <label class="form-label" for="corbata">Color de corbata</label>
 *   </div>
 *
 * **El color se ve, no se lee.** Un desplegable con «Rojo», «Azul» y «Verde»
 * escritos obliga a traducir de la palabra al color cada vez que se busca algo
 * en la cámara; el cuadro pintado se reconoce de un vistazo. Por eso el campo
 * lleva el color de fondo y las opciones son cuadros y no líneas de texto.
 *
 * El nombre sigue estando: dentro del campo, escrito encima del color. Hace
 * falta para quien no distingue esos dos colores -uno de cada doce hombres no
 * separa el rojo del verde- y para quien navega con lector de pantalla.
 *
 * ## Qué se envía
 *
 * El **código** de la opción, no el color. Igual que en el campo de tarjeta: el
 * input visible pierde el `name` y lo hereda uno oculto. El hexadecimal es cosa
 * del catálogo y puede corregirse; el código es lo que identifica la opción.
 *
 * Sin `data-company-color-options` se usa una paleta corriente y entonces el
 * código ES el hexadecimal, que es lo que se espera de un campo de color suelto.
 *
 * ## Vacío
 *
 * El campo se deja vaciar salvo que lleve `required`. Un color suele ser un dato
 * opcional -no todo lo que llega viene marcado-, y sin forma de retirarlo, un
 * clic por error se queda puesto para siempre.
 */

import { syncLabel } from './floating-labels.js';
import { placePanel, closeOnViewportChange } from './place-panel.js';

const ATTR = 'data-company-color-init';
const OPTIONS_ATTR = 'data-company-color-options';

/**
 * Paleta de reserva, para cuando nadie pasa opciones. Son los colores planos de
 * Material: se distinguen bien entre sí impresos y en pantalla.
 */
const DEFAULT_PALETTE = [
  '#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#2196f3',
  '#03a9f4', '#00bcd4', '#009688', '#4caf50', '#8bc34a', '#cddc39',
  '#ffeb3b', '#ffc107', '#ff9800', '#ff5722', '#795548', '#607d8b',
  '#000000', '#9e9e9e', '#e0e0e0', '#ffffff',
];

/**
 * Un color en `#rrggbb`, o cadena vacía si no se reconoce.
 *
 * Se acepta la forma corta -`#abc`- porque se escribe a mano en los catálogos.
 */
export function normalizeColor(value) {
  const text = String(value || '').trim();

  if (/^#[0-9a-f]{6}$/i.test(text)) return text.toLowerCase();

  if (/^#[0-9a-f]{3}$/i.test(text)) {
    const [, r, g, b] = text.toLowerCase();
    return `#${r}${r}${g}${g}${b}${b}`;
  }

  return '';
}

/**
 * Negro o blanco, el que se lea encima de ese fondo.
 *
 * Es la luminancia relativa de la WCAG, la misma cuenta con la que se miden los
 * contrastes: el amarillo es claro aunque su componente rojo sea alto, y hay que
 * escribir encima en negro.
 */
export function inkOver(color) {
  const hex = normalizeColor(color);
  if (!hex) return '';

  const channel = (from) => {
    const c = parseInt(hex.slice(from, from + 2), 16) / 255;

    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };

  const luminance = 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);

  return luminance > 0.45 ? '#000000' : '#ffffff';
}

/** Las opciones declaradas, o la paleta de reserva. */
function readOptions(wrapper) {
  const raw = wrapper.getAttribute(OPTIONS_ATTR);
  let declared = [];

  if (raw) {
    try {
      declared = JSON.parse(raw);
    } catch (error) {
      // Un JSON roto no puede dejar el campo sin abrir: se sigue con la paleta
      // de reserva, que es lo que había antes de declarar nada.
      declared = [];
    }
  }

  const options = (Array.isArray(declared) ? declared : [])
    .map((option) => {
      if (typeof option === 'string') {
        const color = normalizeColor(option);

        return color ? { value: color, label: color, color } : null;
      }

      if (!option || typeof option !== 'object') return null;

      const color = normalizeColor(option.color || option.value);
      const value = String(option.value == null ? color : option.value);

      // Una opción sin color no se puede pintar, y este campo es el color.
      return color && value ? { value, label: String(option.label || value), color } : null;
    })
    .filter(Boolean);

  if (options.length) return options;

  return DEFAULT_PALETTE.map((color) => ({ value: color, label: color, color }));
}

class ColorInput {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.input = wrapper.querySelector('input');
    if (!this.input) throw new Error('company-theme: campo de color sin <input>');

    this.options = readOptions(wrapper);
    this.required = this.input.hasAttribute('required');
    this.name = this.input.getAttribute('name') || '';
    this.panel = null;
    this.unwatch = null;

    // El código elegido, antes de que `_paint` escriba el nombre encima. Sin
    // esto, un campo sin `name` -uno de solo lectura- se quedaba sin saber qué
    // traía puesto en cuanto se pintaba.
    this.input.dataset.value = this.input.value || '';

    // El input visible enseña el NOMBRE y el oculto lleva el código: son dos
    // datos distintos y no caben en un solo campo.
    if (this.name) {
      this.hidden = document.createElement('input');
      this.hidden.type = 'hidden';
      this.hidden.name = this.name;
      this.hidden.value = this.input.value || '';
      this.input.removeAttribute('name');
      wrapper.appendChild(this.hidden);
    }

    // Se escribe eligiendo, no tecleando: el valor es un código del catálogo y
    // no hay nada sensato que teclear.
    this.input.readOnly = true;
    this.input.setAttribute('autocomplete', 'off');
    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-haspopup', 'listbox');
    this.input.setAttribute('aria-expanded', 'false');

    wrapper.classList.add('company-color');

    this._onDocumentClick = (event) => {
      if (this.wrapper.contains(event.target)) return;
      if (this.panel && this.panel.contains(event.target)) return;
      this.close();
    };

    this.input.addEventListener('mousedown', (event) => {
      // Antes del foco: si no, abrir y volver a pulsar cerraba y abría a la vez.
      event.preventDefault();
      this.toggle();
    });

    this.input.addEventListener('keydown', (event) => this._onKeydown(event));

    this._paint();

    wrapper.__companyColor = this;
  }

  /** El código elegido. */
  get value() {
    return this.hidden ? this.hidden.value : this.input.dataset.value || '';
  }

  /** La opción elegida, o `null`. */
  get option() {
    const value = this.value;

    return this.options.find((option) => option.value === value) || null;
  }

  /**
   * Elegir un color.
   *
   * @param {string} value Código de la opción; vacío para quitarlo.
   */
  setValue(value) {
    const text = String(value || '');
    const option = this.options.find((candidate) => candidate.value === text);

    // Un código que no está en el catálogo se descarta: el campo se quedaría
    // diciendo que lleva un color de ningún color.
    const chosen = option ? option.value : '';

    if (this.hidden) this.hidden.value = chosen;
    this.input.dataset.value = chosen;

    this._paint();
    this._announce();
  }

  /** Pinta el campo del color elegido. */
  _paint() {
    const option = this.option;

    this.input.value = option ? option.label : '';
    this.input.style.backgroundColor = option ? option.color : '';
    this.input.style.color = option ? inkOver(option.color) : '';
    this.wrapper.classList.toggle('company-color-set', !!option);

    // MDB sube el label cuando el campo tiene texto, y aquí el texto lo pone el
    // componente: sin esto el label se queda encima del color.
    syncLabel(this.input);
  }

  /** Avisa al formulario, que es quien guarda el borrador. */
  _announce() {
    const target = this.hidden || this.input;

    target.dispatchEvent(new Event('input', { bubbles: true }));
    target.dispatchEvent(new Event('change', { bubbles: true }));
  }

  _onKeydown(event) {
    const keys = ['Enter', ' ', 'ArrowDown', 'ArrowUp'];

    if (keys.indexOf(event.key) !== -1) {
      event.preventDefault();
      this.open();

      const first = this.panel && this.panel.querySelector('.company-color-option');
      if (first) first.focus();

      return;
    }

    if (event.key === 'Escape') this.close();

    // Retroceso y suprimir quitan el color, como en cualquier campo de texto.
    if ((event.key === 'Backspace' || event.key === 'Delete') && !this.required) {
      event.preventDefault();
      this.setValue('');
    }
  }

  toggle() {
    if (this.panel) {
      this.close();

      return;
    }

    this.open();
  }

  open() {
    if (this.panel) return;

    // Uno abierto cada vez: dos paneles flotando a la vez sobre el mismo
    // formulario se solapan y no se sabe cuál está contestando.
    document.querySelectorAll('.company-color-panel').forEach((other) => {
      const owner = other.__companyColorOwner;
      if (owner) owner.close();
    });

    this.panel = this._buildPanel();
    this.panel.__companyColorOwner = this;
    document.body.appendChild(this.panel);

    placePanel(this.input, this.panel);
    this.unwatch = closeOnViewportChange(() => this.close(), this.panel);
    document.addEventListener('click', this._onDocumentClick, true);

    this.input.setAttribute('aria-expanded', 'true');
    this.wrapper.classList.add('company-color-open');
  }

  close() {
    if (!this.panel) return;

    document.removeEventListener('click', this._onDocumentClick, true);
    if (this.unwatch) this.unwatch();
    this.unwatch = null;

    this.panel.remove();
    this.panel = null;

    this.input.setAttribute('aria-expanded', 'false');
    this.wrapper.classList.remove('company-color-open');
  }

  _buildPanel() {
    const panel = document.createElement('div');
    panel.className = 'company-color-panel';

    const grid = document.createElement('div');
    grid.className = 'company-color-grid';
    grid.setAttribute('role', 'listbox');

    const chosen = this.value;

    this.options.forEach((option) => {
      const cell = document.createElement('button');
      cell.type = 'button';
      cell.className = 'company-color-option';
      cell.style.backgroundColor = option.color;
      cell.style.color = inkOver(option.color);
      cell.dataset.value = option.value;
      cell.setAttribute('role', 'option');
      cell.setAttribute('title', option.label);
      // El nombre, no el hexadecimal: «Rojo» se entiende y `#e53935` no.
      cell.setAttribute('aria-label', option.label);
      cell.setAttribute('aria-selected', option.value === chosen ? 'true' : 'false');
      cell.classList.toggle('is-selected', option.value === chosen);

      cell.addEventListener('click', () => {
        this.setValue(option.value);
        this.close();
        this.input.focus();
      });

      grid.appendChild(cell);
    });

    grid.addEventListener('keydown', (event) => this._movePanel(event, grid));

    panel.appendChild(grid);

    if (!this.required) {
      const clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'company-color-clear';
      clear.textContent = 'Sin color';

      clear.addEventListener('click', () => {
        this.setValue('');
        this.close();
        this.input.focus();
      });

      panel.appendChild(clear);
    }

    return panel;
  }

  /** Flechas entre los cuadros, con la rejilla que se esté pintando. */
  _movePanel(event, grid) {
    const cells = Array.from(grid.querySelectorAll('.company-color-option'));
    const current = cells.indexOf(document.activeElement);

    if (event.key === 'Escape') {
      this.close();
      this.input.focus();

      return;
    }

    if (current === -1) return;

    // Cuántos caben por fila lo decide el CSS, así que se cuenta sobre lo
    // pintado: fijar el número aquí rompería las flechas en el móvil, donde la
    // rejilla se estrecha.
    const top = cells[0].offsetTop;
    let perRow = cells.findIndex((cell) => cell.offsetTop > top);
    if (perRow < 1) perRow = cells.length;

    const steps = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: perRow, ArrowUp: -perRow };
    const step = steps[event.key];

    if (step === undefined) return;

    event.preventDefault();

    const next = cells[Math.min(Math.max(current + step, 0), cells.length - 1)];
    if (next) next.focus();
  }

  dispose() {
    this.close();

    if (this.hidden) {
      this.input.setAttribute('name', this.name);
      this.hidden.remove();
    }

    this.input.style.backgroundColor = '';
    this.input.style.color = '';
    this.wrapper.classList.remove('company-color', 'company-color-set');
    delete this.wrapper.__companyColor;
  }
}

/**
 * @param {ParentNode} [root=document]
 */
export function setupColorInputs(root = document) {
  if (!root || typeof root.querySelectorAll !== 'function') return;

  root.querySelectorAll(`[${ATTR}]`).forEach((wrapper) => {
    if (wrapper.__companyColor) return;
    new ColorInput(wrapper);
  });
}

export { ColorInput };
export default setupColorInputs;
